<template>
  <div>
    <section class="ar-panel ar-panel--add">
      <header class="ar-panel__header">
        <div class="ar-panel__title">
          <feather-icon
            icon="UserPlusIcon"
            size="18"
          />
          {{ config.addTitle }}
        </div>
        <span class="ar-panel__hint">
          {{ config.addHint }}
        </span>
      </header>
      <div class="ar-add">
        <v-select
          v-model="selectedUser"
          :options="availableUsers"
          :disabled="addLoad || usersLoading"
          :loading="usersLoading"
          placeholder="Search user by name or email…"
          class="ar-add__select"
        >
          <template #no-options>
            No more users available to add
          </template>
        </v-select>
        <button
          type="button"
          class="ar-btn ar-btn--primary"
          :disabled="!selectedUser || addLoad"
          @click="addMember"
        >
          <b-spinner
            v-if="addLoad"
            small
            variant="light"
          />
          <template v-else>
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            Add
          </template>
        </button>
      </div>
    </section>

    <section class="ar-panel">
      <header class="ar-panel__header ar-panel__header--row">
        <div class="ar-panel__title">
          <feather-icon
            icon="ClipboardIcon"
            size="18"
          />
          {{ config.listTitle }}
        </div>
        <div class="ar-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="ar-search__icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="ar-search__input"
            placeholder="Search by name or email…"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="ar-search__clear"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>
      </header>

      <div
        v-if="dataLoad"
        class="ar-loading"
      >
        <div class="ar-loading__spinner" />
        <p class="mb-0 text-muted">
          Loading {{ config.memberPlural }}…
        </p>
      </div>

      <div
        v-else-if="!filteredMembers.length"
        class="ar-empty"
      >
        <div class="ar-empty__icon">
          <feather-icon
            icon="InboxIcon"
            size="28"
          />
        </div>
        <h5 class="mb-50">
          {{ searchQuery ? config.emptySearchTitle : config.emptyTitle }}
        </h5>
        <p class="mb-0 text-muted">
          {{ searchQuery ? 'Try a different search.' : config.emptyHint }}
        </p>
      </div>

      <div
        v-else
        class="ar-grid"
      >
        <article
          v-for="(member, index) in filteredMembers"
          :key="member.id"
          class="ar-card"
        >
          <div
            class="ar-card__accent"
            :class="`ar-card__accent--${index % 4}`"
          />
          <div class="ar-card__top">
            <div
              class="ar-card__avatar"
              :class="`ar-card__avatar--${index % 4}`"
            >
              {{ avatarText(member.name) }}
            </div>
            <button
              type="button"
              class="ar-card__delete"
              :title="config.removeButtonTitle"
              :disabled="removeLoadId === member.id"
              @click="confirmRemove(member)"
            >
              <b-spinner
                v-if="removeLoadId === member.id"
                small
                variant="danger"
              />
              <feather-icon
                v-else
                icon="Trash2Icon"
                size="16"
              />
            </button>
          </div>
          <h4 class="ar-card__name">
            {{ member.name }}
          </h4>
          <a
            :href="`mailto:${member.email}`"
            class="ar-card__email"
          >
            <feather-icon
              icon="MailIcon"
              size="14"
            />
            {{ member.email }}
          </a>
          <div
            v-if="member.created_at"
            class="ar-card__meta"
          >
            <span class="ar-card__chip">
              <feather-icon
                icon="CalendarIcon"
                size="12"
              />
              {{ formatDateValue(member.created_at) }}
            </span>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import VSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

export default {
  name: 'AssignPersonalPanel',
  components: {
    'v-select': VSelect,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    usersLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      members: [],
      dataLoad: false,
      addLoad: false,
      removeLoadId: null,
      selectedUser: null,
      searchQuery: '',
    }
  },
  computed: {
    memberList() {
      return Array.isArray(this.members) ? this.members : []
    },
    filteredMembers() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.memberList
      return this.memberList.filter(item => (
        String(item.name || '').toLowerCase().includes(q)
        || String(item.email || '').toLowerCase().includes(q)
      ))
    },
    existingUserIds() {
      return new Set(this.memberList.map(item => Number(item.user_id)).filter(Boolean))
    },
    availableUsers() {
      const list = this.$store.getters['generalData/assignList/assignList'] || []
      return list
        .filter(user => !this.existingUserIds.has(Number(user?.value?.id)))
        .map(user => ({
          value: user.value.id,
          label: user.label,
        }))
    },
  },
  mounted() {
    this.fetchMembers()
  },
  methods: {
    formatDateValue(value) {
      return formatDate(value) || value
    },
    avatarText(name) {
      if (!name) return '?'
      return String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    normalizeList(payloadData) {
      if (Array.isArray(payloadData)) return payloadData
      if (Array.isArray(payloadData?.data)) return payloadData.data
      return Object.values(payloadData || {}).find(Array.isArray) || []
    },
    async fetchMembers() {
      this.dataLoad = true
      try {
        const response = await axios.get(this.config.endpoint)
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          this.members = this.normalizeList(responseData?.data)
        }
      } catch (error) {
        this.members = []
        this.showError(error, this.config.loadError)
      } finally {
        this.dataLoad = false
      }
    },
    async addMember() {
      const userId = this.selectedUser?.value
      if (!userId || this.addLoad) return

      this.addLoad = true
      try {
        const response = await axios.post(this.config.endpoint, {
          user_id: userId,
        })
        if (response.status === 200 || response.status === 201) {
          this.selectedUser = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.config.addSuccess,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          await this.fetchMembers()
        }
      } catch (error) {
        this.showError(error, this.config.addError)
      } finally {
        this.addLoad = false
      }
    },
    confirmRemove(member) {
      this.$swal({
        title: this.config.removeTitle,
        text: member.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
      }).then(result => {
        if (result.isConfirmed) {
          this.removeMember(member)
        }
      })
    },
    async removeMember(member) {
      this.removeLoadId = member.id
      try {
        const response = await axios.delete(`${this.config.endpoint}/${member.id}`)
        if (response.status === 200 || response.status === 204) {
          this.members = this.memberList.filter(item => item.id !== member.id)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.config.removeSuccess,
              icon: 'Trash2Icon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        this.showError(error, this.config.removeError)
      } finally {
        this.removeLoadId = null
      }
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList) {
              const [firstMessage] = firstList
              if (firstMessage) message = firstMessage
            }
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>
